import React, { useEffect, useRef, useState, useCallback } from 'react';
import styled from 'styled-components';
import SimpleButton from '../molecules/Button/SimpleButton';
import Icon from '../Icon';
import theme from '../theme';
import Typo from '../Typo';

const Wrapper = styled.div`
  position: relative;
  display: inline-flex;
`;

const MainAction = styled(SimpleButton)`
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  border-right: 1px solid rgba(255, 255, 255, 0.3);
`;

const ArrowAction = styled(SimpleButton)`
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  min-width: unset;

  svg {
    transition: transform 0.2s;
  }
  &[aria-expanded='true'] svg {
    transform: rotate(180deg);
  }
`;

const Dropdown = styled.div`
  position: absolute;
  ${({ $placement }) => ($placement === 'top' ? 'bottom: calc(100% + 4px);' : 'top: calc(100% + 4px);')}
  right: 0;
  min-width: 240px;
  background: ${theme.colors.NEU_0};
  border-radius: 10px;
  box-shadow: ${theme.shadows.BOX_SHADOW_SMALL};
  border: 1px solid ${theme.colors.NEU_200};
  overflow: hidden;
  z-index: 10;
`;

const Option = styled.button`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  padding: 10px 14px;
  border: none;
  background: ${({ $selected }) => ($selected ? theme.colors.NEU_50 : 'none')};
  cursor: pointer;
  text-align: left;
  font-family: inherit;

  &:hover {
    background: ${theme.colors.NEU_50};
  }
  &:not(:last-child) {
    border-bottom: 1px solid ${theme.colors.NEU_100};
  }
`;

const OptionIcon = styled.span`
  flex-shrink: 0;
  margin-top: 2px;
  color: ${theme.colors.NEU_600};
`;

const OptionText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ArrowIcon = () => (
  <svg viewBox='0 0 24 24' fill='currentColor' width='18' height='18'>
    <path d='M7 10l5 5 5-5z' />
  </svg>
);

const SplitButton = ({ options = [], onSubmit, preselectedIndex = 0, disabled, size = 'medium', variant = 'secondary', placement = 'top' }) => {
  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(preselectedIndex >= 0 ? preselectedIndex : 0);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (preselectedIndex >= 0) setSelectedIndex(preselectedIndex);
  }, [preselectedIndex]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleSelect = useCallback((index) => {
    setSelectedIndex(index);
    setOpen(false);
  }, []);

  const selected = options[selectedIndex];

  return (
    <Wrapper ref={wrapperRef}>
      {open && (
        <Dropdown $placement={placement}>
          {options.map((option, index) => {
            const IconComponent = option.icon && Icon[option.icon];
            return (
              <Option key={option.key} $selected={index === selectedIndex} onClick={() => handleSelect(index)}>
                {IconComponent && (
                  <OptionIcon>
                    <IconComponent size='small' />
                  </OptionIcon>
                )}
                <OptionText>
                  <Typo.SmallText fontWeight='500'>{option.label}</Typo.SmallText>
                  {option.description && <Typo.Info color={theme.colors.NEU_500}>{option.description}</Typo.Info>}
                </OptionText>
              </Option>
            );
          })}
        </Dropdown>
      )}

      <MainAction variant={variant} size={size} bgColor={selected?.color} startIcon={selected?.icon} disabled={disabled} onClick={() => onSubmit(selected)}>
        {selected?.label}
      </MainAction>
      <ArrowAction
        variant={variant}
        size={size}
        bgColor={selected?.color}
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup='true'
        aria-label='select option'
        onClick={() => setOpen((prev) => !prev)}
      >
        <ArrowIcon />
      </ArrowAction>
    </Wrapper>
  );
};

export default SplitButton;
